// The feedback widget — a persistent, low-friction way to say "this is missing".
//
// It lives in the console chrome (not on a settings page nobody opens) because
// the moment you want to report something is the moment you hit the wall, and
// any navigation between those two things loses most reports.
//
// Deliberately asks TWO questions, not one:
//   - what do you want           (the request)
//   - what were you trying to do (the goal)
// The second is the one that's actually actionable. "Add webhooks" tells us
// nothing about which webhook; "I was trying to get notified when a user signs
// up" tells us what to build, and sometimes tells us it already exists.

import {
	Button,
	Group,
	Modal,
	SegmentedControl,
	Stack,
	Text,
	Textarea,
	TextInput,
	Tooltip,
} from "@mantine/core";
import { IconBulb, IconCheck } from "@tabler/icons-react";
import { useState } from "react";
import { useSession } from "../session/SessionProvider.tsx";
import { c, radius } from "../design/tokens.ts";

type Kind = "feature" | "bug" | "other";

export function FeedbackWidget() {
	const { client, signedIn } = useSession();
	const [open, setOpen] = useState(false);
	const [kind, setKind] = useState<Kind>("feature");
	const [message, setMessage] = useState("");
	const [goal, setGoal] = useState("");
	const [busy, setBusy] = useState(false);
	const [sentId, setSentId] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	// Signed-out visitors have no verified identity, and the function requires
	// one (it's what the per-reporter quota is keyed on). Rather than show a
	// button that always fails, don't show it.
	if (!signedIn) return null;

	async function submit() {
		if (!client || !message.trim()) return;
		setBusy(true);
		setError(null);
		try {
			const res = (await client.fn("submit-feedback")(
				{ message: message.trim(), goal: goal.trim() || undefined, kind },
				{ timeoutMs: 30_000 },
			)) as { id: string };
			setSentId(res.id);
			setMessage("");
			setGoal("");
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setBusy(false);
		}
	}

	function close() {
		setOpen(false);
		// Reset the thank-you on the way out, so re-opening starts clean rather
		// than showing a stale receipt for something sent ten minutes ago.
		setTimeout(() => {
			setSentId(null);
			setError(null);
		}, 200);
	}

	return (
		<>
			<Tooltip label="Something missing? Tell us." position="left" withArrow>
				<Button
					onClick={() => setOpen(true)}
					leftSection={<IconBulb size={16} />}
					variant="default"
					radius="xl"
					size="sm"
					style={{
						position: "fixed",
						right: 20,
						bottom: 20,
						zIndex: 200,
						boxShadow: "0 4px 16px rgba(20,20,31,0.10)",
						background: c.bg,
						borderColor: c.border,
					}}
				>
					Feedback
				</Button>
			</Tooltip>

			<Modal opened={open} onClose={close} title="Tell us what's missing" radius={radius.card} centered>
				{sentId ? (
					<Stack gap="sm" py="sm">
						<Group gap={8}>
							<IconCheck size={20} color={c.blue} />
							<Text fw={700}>Got it — thank you.</Text>
						</Group>
						<Text size="sm" c="dimmed">
							We read every one of these. Reference <code>{sentId}</code>.
						</Text>
						<Button onClick={close} variant="default" mt="xs">
							Close
						</Button>
					</Stack>
				) : (
					<Stack gap="md">
						<SegmentedControl
							fullWidth
							value={kind}
							onChange={(v) => setKind(v as Kind)}
							data={[
								{ label: "Feature request", value: "feature" },
								{ label: "Bug", value: "bug" },
								{ label: "Something else", value: "other" },
							]}
						/>
						<Textarea
							label={kind === "bug" ? "What went wrong?" : "What would you like?"}
							placeholder={
								kind === "bug"
									? "What you did, what happened, what you expected."
									: "The thing you wish s3p did."
							}
							autosize
							minRows={4}
							value={message}
							onChange={(e) => setMessage(e.currentTarget.value)}
						/>
						<TextInput
							label="What were you trying to do?"
							description="Optional — but it's the part that tells us what to actually build."
							placeholder="e.g. let users download a big file without it going through the tab"
							value={goal}
							onChange={(e) => setGoal(e.currentTarget.value)}
						/>
						{error && (
							<Text size="sm" c="red">
								{error}
							</Text>
						)}
						<Group justify="flex-end">
							<Button variant="subtle" onClick={close}>
								Cancel
							</Button>
							<Button onClick={() => void submit()} loading={busy} disabled={!message.trim()}>
								Send
							</Button>
						</Group>
					</Stack>
				)}
			</Modal>
		</>
	);
}
